import { useNavigate } from "react-router-dom";
import { Building2, Clock, FileText, Tag } from "lucide-react";
import { Card } from "@/components/ui/card";
import { EstadoBadge, PrioridadBadge } from "./estado-badge";
import type { Licitacion } from "@/types/licitacion";

function formatCurrency(value: number | null): string {
  if (value === null) return "\u2014";
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value);
}

function getDaysRemaining(fechaLimite: string | null): { days: number; color: string } | null {
  if (!fechaLimite) return null;
  const diff = Math.ceil((new Date(fechaLimite).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  const color = diff < 7 ? "text-red-600" : diff < 14 ? "text-yellow-600" : "text-green-600";
  return { days: diff, color };
}

export function LicitacionCard({ licitacion }: { licitacion: Licitacion }) {
  const navigate = useNavigate();
  const deadline = getDaysRemaining(licitacion.fecha_limite);

  return (
    <Card
      className="p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => navigate(`/licitaciones/${licitacion.id}`)}
    >
      <h3 className="font-semibold text-gray-900 truncate">{licitacion.titulo}</h3>
      <div className="flex items-center gap-1.5 mt-2 text-sm text-gray-600">
        <Building2 className="w-3.5 h-3.5 text-gray-400" />
        <span className="truncate">{licitacion.organismo}</span>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <PrioridadBadge prioridad={licitacion.prioridad} />
        <span className="text-xs text-gray-500">{licitacion.tipo_contrato.replace(/_/g, " ")}</span>
      </div>
      <p className="text-sm font-medium text-gray-800 mt-2">{formatCurrency(licitacion.presupuesto_base)}</p>
      <div className="flex items-center justify-between mt-3">
        <EstadoBadge estado={licitacion.estado} />
        {deadline && (
          <div className={`flex items-center gap-1 text-xs ${deadline.color}`}>
            <Clock className="w-3.5 h-3.5" />
            <span>{deadline.days > 0 ? `${deadline.days} d\u00edas` : "Vencida"}</span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <FileText className="w-3.5 h-3.5" />
          <span>{licitacion.document_count} docs</span>
        </div>
        {(licitacion.etiquetas ?? []).length > 0 && (
          <div className="flex items-center gap-1">
            <Tag className="w-3 h-3 text-gray-400" />
            {(licitacion.etiquetas ?? []).slice(0, 2).map((tag) => (
              <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
